// ============================================================================
// ACTION TYPE SERVICE
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { actionTypeEndpoints, ACTION_TYPE_BASE_PATH } from '../endpoints/actionType.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  ActionType,
  ActionTypeListItem,
  CreateActionTypeRequest,
  UpdateActionTypeRequest,
  ActionTypeSearchRequest,
} from '../types/actionType.types';

class ActionTypeService {
  async getAll(params?: ActionTypeSearchRequest): Promise<PaginatedResponse<ActionTypeListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(ACTION_TYPE_BASE_PATH, params)
      : actionTypeEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<ActionType>> {
    const response = await get<ApiResponse<ActionType>>(actionTypeEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<ActionType>> {
    const response = await get<ApiResponse<ActionType>>(actionTypeEndpoints.getByCode(code));
    return response.data;
  }

  async create(data: CreateActionTypeRequest): Promise<ApiResponse<ActionType>> {
    const response = await post<ApiResponse<ActionType>>(actionTypeEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateActionTypeRequest): Promise<ApiResponse<ActionType>> {
    const response = await put<ApiResponse<ActionType>>(actionTypeEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(actionTypeEndpoints.delete(id));
    return response.data;
  }

  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    const endpoint = excludeId
      ? `${actionTypeEndpoints.validateCode(code)}?excludeId=${excludeId}`
      : actionTypeEndpoints.validateCode(code);
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

export const actionTypeService = new ActionTypeService();
export default actionTypeService;
